/**
 * PROVIDER PASSPORT — the decision-quality view of one network organization.
 *
 * The passport is assembled ONLY from records joined by a CONFIRMED identity
 * link. Proposed, disputed and unresolved links contribute no facts and are
 * shown separately, because merging two organizations is a human decision
 * that happens in the backend — never implicitly in a browser.
 *
 * Where the backend states two different values for one field it reports
 * CONFLICT and picks no winner. Neither does this page.
 *
 * Sections the backend does not carry (CSI/NAICS, project history,
 * organization-level insurance/bonding/capacity, aggregate verification)
 * are absent here rather than faked — a passport that implies coverage it
 * does not have is worse than one that says "not recorded".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import exchangeApi from "../services/exchangeService";
import { classifyWorkflowError } from "../lib/workflowErrors";
import { hasCapability } from "../lib/capabilities";
import { createSequencer } from "../lib/sequencer";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  SectionLoading,
  SectionError,
  SectionEmpty,
  FeatureDisabled,
  NotEntitled,
  NoAccess,
  UnknownValue,
} from "../components/workflow/WorkflowStates";
import {
  readField,
  verificationDisplay,
  isVerifiedStatus,
  linkStateDisplay,
  summarizeFreshness,
  ageInDays,
  recordTypeLabel,
  conflictFieldLabel,
  listingTypeLabel,
  shortId,
  formatDay,
} from "../features/exchange/exchangePresentation";
import RelationshipPanel from "../features/exchange/RelationshipPanel";
import CommercialLedgerPanel from "../features/exchange/CommercialLedgerPanel";
import ScopeOutcomesPanel from "../features/exchange/ScopeOutcomesPanel";
import {
  ArrowLeft,
  IdCard,
  Flame,
  Link2,
  ShieldCheck,
  MapPin,
  Mail,
  Hammer,
  Store,
  Clock,
  AlertTriangle,
  Loader2,
} from "lucide-react";

/**
 * The three decisions the backend accepts (DECISION_STATES). UNRESOLVED is
 * not a formality: a DISPUTED link can never go straight to CONFIRMED, so
 * "move back to unresolved" is the only route out of a dispute. Without it
 * the UI would offer a state it could not leave.
 */
const DECISION_COPY = {
  CONFIRMED: {
    title: "Confirm identity match",
    body:
      "Confirming links this record to the organization, and its facts will then appear on the passport. One confirmed link is allowed per record.",
    noteRequired: false,
    placeholder: "Optional context for the record",
    toast: "Identity link confirmed.",
  },
  DISPUTED: {
    title: "Dispute identity match",
    body:
      "A dispute records a standing disagreement. It deletes nothing, and a disputed link cannot be confirmed until it is first moved back to unresolved.",
    noteRequired: true,
    placeholder: "What disagrees — different company, different branch, wrong record?",
    toast: "Dispute recorded.",
  },
  UNRESOLVED: {
    title: "Move back to unresolved",
    body:
      "This withdraws whatever the link currently states — a confirmation or a dispute — without asserting anything new. An unresolved link contributes no facts to the passport, and it frees the record to be confirmed against a different organization. It can be confirmed or disputed again later.",
    noteRequired: false,
    placeholder: "Optional context for the record",
    toast: "Identity link moved back to unresolved.",
  },
};

const VERIFICATION_TONE = {
  verified: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  self: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  bad: "bg-red-500/20 text-red-400 border-red-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
  proposed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

/** A composed field: one value, UNKNOWN, or an unresolved CONFLICT. */
const ComposedField = ({ label, field, icon: Icon }) => {
  const read = readField(field);
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </p>
      {read.state === "CONFLICT" ? (
        <div className="space-y-0.5">
          <span className="text-sm font-medium text-red-400 inline-flex items-center gap-1">
            <Flame className="w-3.5 h-3.5" />
            Records disagree
          </span>
          {read.candidates.map((candidate, i) => (
            <p key={i} className="text-[11px] text-muted-foreground">
              {String(candidate?.value)}
              {candidate?.source?.recordType
                ? ` — ${recordTypeLabel(candidate.source.recordType)}`
                : ""}
            </p>
          ))}
        </div>
      ) : read.state === "UNKNOWN" ? (
        <UnknownValue reason="No linked record states this." />
      ) : (
        <p className="text-sm font-medium break-words">{String(read.value)}</p>
      )}
    </div>
  );
};

const ProviderPassportPage = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { user, capabilities, capabilitiesEstablished } = useAuth();

  const [passport, setPassport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // {link, decision}
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [relationship, setRelationship] = useState(null);
  const [relationshipError, setRelationshipError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [outcomes, setOutcomes] = useState(null);
  const [outcomesError, setOutcomesError] = useState(null);
  const seq = useRef(createSequencer()).current;
  const relationshipSeq = useRef(createSequencer()).current;
  const outcomesSeq = useRef(createSequencer()).current;

  // Served by the session, not mirrored from routes/exchange.py.
  const canDecide = hasCapability(capabilities, "exchange.decide");
  // Recording a relationship statement is the same authority set as deciding
  // an identity link — an estimator may read this page but not write to it.
  const canRecord = canDecide;

  const load = useCallback(async () => {
    const ticket = seq.next();
    setLoading(true);
    setError(null);
    setPassport(null);
    try {
      const data = await exchangeApi.getProvider(organizationId);
      if (seq.isCurrent(ticket)) setPassport(data);
    } catch (err) {
      if (seq.isCurrent(ticket)) setError(classifyWorkflowError(err));
    } finally {
      if (seq.isCurrent(ticket)) setLoading(false);
    }
  }, [organizationId, seq]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The relationship context is a separate read with its own refusals — the
   * passport can load while this one 404s (no identity link at all) or is
   * switched off. It is loaded independently so one does not hide the other.
   */
  const loadRelationship = useCallback(async () => {
    const ticket = relationshipSeq.next();
    setRelationship(null);
    setRelationshipError(null);
    try {
      const data = await exchangeApi.getProviderRelationship(organizationId);
      if (relationshipSeq.isCurrent(ticket)) setRelationship(data);
    } catch (err) {
      if (relationshipSeq.isCurrent(ticket)) setRelationshipError(classifyWorkflowError(err));
    }
  }, [organizationId, relationshipSeq]);

  useEffect(() => {
    loadRelationship();
  }, [loadRelationship]);

  /**
   * The outcome ledger is a third independent read with its own refusals.
   * Its 404 means something different from the passport's — no identity link
   * at all in this tenant, so the ledger is unavailable rather than empty —
   * and it must not be able to blank the identity facts above it.
   */
  const loadOutcomes = useCallback(async () => {
    const ticket = outcomesSeq.next();
    setOutcomes(null);
    setOutcomesError(null);
    try {
      const data = await exchangeApi.getProviderOutcomes(organizationId);
      if (outcomesSeq.isCurrent(ticket)) setOutcomes(data);
    } catch (err) {
      if (outcomesSeq.isCurrent(ticket)) setOutcomesError(classifyWorkflowError(err));
    }
  }, [organizationId, outcomesSeq]);

  useEffect(() => {
    loadOutcomes();
  }, [loadOutcomes]);

  /**
   * Append one human statement. The backend takes the record pair, not the
   * organization id, so the statement is filed against a CONFIRMED linked
   * record — an unconfirmed link cannot carry an attribution and the backend
   * refuses one with 422.
   */
  const recordStatement = async (body) => {
    const confirmedVendor = (relationship?.identity?.confirmedVendorIds || [])[0];
    const confirmedSub = (relationship?.identity?.confirmedSubcontractorCompanyIds || [])[0];
    setRecording(true);
    try {
      await exchangeApi.recordRelationshipEvent({
        // Prefer a confirmed record; fall back to the organization itself,
        // which the backend accepts as its own record type.
        recordType: confirmedVendor
          ? "vendor"
          : confirmedSub
            ? "subcontractor_company"
            : "network_organization",
        recordId: confirmedVendor || confirmedSub || organizationId,
        ...body,
      });
      toast.success("Statement recorded.");
      await loadRelationship();
      return true;
    } catch (err) {
      const classified = classifyWorkflowError(err);
      toast.error(classified.message);
      return false;
    } finally {
      setRecording(false);
    }
  };

  // A note is written against ONE link. Carrying it to the next dialog would
  // file text about link A as link B's audited decision_note, and would also
  // pre-satisfy the "a dispute must state its reason" guard with a reason
  // that was never about this link.
  const closeDecision = useCallback(() => {
    setDecision(null);
    setNote("");
  }, []);

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.decision === "DISPUTED" && !note.trim()) {
      toast.error("Recording a dispute requires a note saying what disagrees.");
      return;
    }
    setDeciding(true);
    try {
      await exchangeApi.decideIdentityLink(
        decision.link.linkId,
        decision.decision,
        note.trim() || undefined
      );
      toast.success(DECISION_COPY[decision.decision]?.toast || "Identity decision recorded.");
      closeDecision();
      // BOTH reads depend on which links are CONFIRMED — the passport for its
      // facts, the relationship context for the record a statement would be
      // filed against. Reloading only the passport would leave the next
      // statement aimed at a record that is no longer confirmed.
      load();
      loadRelationship();
      // The ledger resolves this firm's records at READ TIME through
      // CONFIRMED links only, so a decision changes which rows it can join
      // on at all — a stale ledger here would attribute facts through a
      // link the user just disputed.
      loadOutcomes();
    } catch (err) {
      const classified = classifyWorkflowError(err);
      toast.error(classified.message);
      if (classified.kind === "conflict") {
        load();
        loadRelationship();
        loadOutcomes();
      }
    } finally {
      setDeciding(false);
    }
  };

  if (loading) return <SectionLoading label="Assembling the provider passport…" />;

  if (error) {
    const back = (
      <Button variant="ghost" size="sm" onClick={() => navigate("/exchange/providers")}>
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Providers
      </Button>
    );
    return (
      <div className="space-y-4">
        {back}
        {error.kind === "not-entitled" ? (
          <NotEntitled feature="The Exchange" message={error.message} />
        ) : error.kind === "disabled" ? (
          <FeatureDisabled feature="The Exchange" />
        ) : error.kind === "no-access" ? (
          <NoAccess message={error.message} />
        ) : error.kind === "not-found" ? (
          <SectionEmpty
            icon={IdCard}
            title="No passport for this organization"
            hint="This organization has no identity link in your tenant, so there are no records to assemble a passport from."
          />
        ) : (
          <SectionError error={error} onRetry={load} label="The passport failed to load" />
        )}
      </div>
    );
  }

  if (!passport) return null;

  const org = passport.networkOrganization || {};
  const linkage = passport.linkage || {};
  const conflicts = passport.conflicts || [];
  const freshness = summarizeFreshness(passport.freshness);
  const listings = passport.marketplace?.listings || [];
  const copy = decision ? DECISION_COPY[decision.decision] : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-1">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-7 text-xs"
          onClick={() => navigate("/exchange/providers")}
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" />
          Providers
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
              <IdCard className="w-6 h-6 text-primary" />
              {(passport.identity?.names || [])[0]?.value || org.displayName || "Provider"}
            </h1>
            <p className="text-xs text-muted-foreground">
              Network identity anchor: {org.displayName}
              {org.status ? ` · ${org.status}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={VERIFICATION_TONE.verified}>
              {linkage.confirmedCount || 0} confirmed record
              {(linkage.confirmedCount || 0) === 1 ? "" : "s"}
            </Badge>
            {(linkage.proposedCount || 0) > 0 && (
              <Badge variant="outline" className={VERIFICATION_TONE.proposed}>
                {linkage.proposedCount} unconfirmed
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Conflicts first — an unresolved disagreement is decision-relevant */}
      {conflicts.length > 0 && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="py-3 px-4 space-y-1">
            <p className="text-sm font-semibold text-red-400 flex items-center gap-1.5">
              <Flame className="w-4 h-4" />
              Linked records disagree on {conflicts.length} field
              {conflicts.length === 1 ? "" : "s"}
            </p>
            {conflicts.map((conflict, i) => (
              <div key={i} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {conflictFieldLabel(conflict.field)}:
                </span>{" "}
                {(conflict.candidates || []).map((c) => String(c?.value)).join("  ·  ")}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground pt-1">
              OMNΛS does not choose between them. Resolve it in the underlying records.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Identity */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <IdCard className="w-4 h-4 text-primary" />
              Identity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ComposedField label="Legal name" field={passport.identity?.legalName} />
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Names on linked records
              </p>
              {(passport.identity?.names || []).length === 0 ? (
                <UnknownValue reason="No confirmed record carries a name." />
              ) : (
                <div className="space-y-0.5">
                  {passport.identity.names.map((name, i) => (
                    <p key={i} className="text-sm">
                      {name.value}
                      <span className="text-[11px] text-muted-foreground">
                        {" "}
                        — {recordTypeLabel(name.source?.recordType)}
                      </span>
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ComposedField label="Email" field={passport.contact?.email} icon={Mail} />
              <ComposedField label="Phone" field={passport.contact?.phone} />
            </div>
            <ComposedField label="Website" field={passport.contact?.website} />
            {passport.identity?.basis && (
              <p className="text-[10px] text-muted-foreground">{passport.identity.basis}</p>
            )}
          </CardContent>
        </Card>

        {/* Geography + trades */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Where & what
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <ComposedField label="City" field={passport.geography?.city} />
              <ComposedField label="State" field={passport.geography?.state} />
              <ComposedField label="Postal" field={passport.geography?.postalCode} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Hammer className="w-3 h-3" />
                Trades & specialties
              </p>
              {(passport.trades?.values || []).length === 0 ? (
                <UnknownValue reason="No linked record states a trade or specialty." />
              ) : (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {passport.trades.values.map((trade, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-muted/50 border-border font-normal"
                      title={`${trade.kind} — ${trade.source?.recordType || "unknown source"}`}
                    >
                      {trade.value}
                      {trade.code ? ` (${trade.code})` : ""}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Certifications
              </p>
              {(passport.certifications?.values || []).length === 0 ? (
                <UnknownValue reason="No linked record lists a certification." />
              ) : (
                <div className="space-y-0.5 pt-1">
                  {passport.certifications.values.map((cert, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-sm">{String(cert.value)}</span>
                      <Badge
                        variant="outline"
                        className={`${VERIFICATION_TONE.unknown} font-normal text-[10px]`}
                        title="OMNΛS holds no verification record for organization-level certifications."
                      >
                        Not verified by OMNΛS
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* The working relationship — tenant-private, and never a score */}
      {relationship ? (
        <RelationshipPanel
          context={relationship}
          canRecord={canRecord}
          recording={recording}
          onRecord={recordStatement}
          organizationName={org.displayName}
        />
      ) : relationshipError && relationshipError.kind !== "not-found" ? (
        <Card className="border-border">
          <CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">
              {relationshipError.kind === "not-entitled"
                ? "Relationship history is not part of your plan."
                : relationshipError.kind === "disabled"
                  ? "Relationship history is not enabled in this environment."
                  : relationshipError.kind === "no-access"
                    ? "Your role does not include relationship history."
                    : "Relationship history did not load. The identity facts above are unaffected."}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Scope this firm owned, and what was observed on it. A third
          independent read: its 404 means no identity link exists in this
          tenant, which routes to identity resolution rather than to an
          empty ledger, so it is shown rather than hidden. */}
      {/* The commercial half, from the SAME ledger read the panel below
          uses. A second request would put two moments of one ledger side by
          side; the outcomes endpoint carries both halves in one response. */}
      <CommercialLedgerPanel payload={outcomes} />

      <ScopeOutcomesPanel
        payload={outcomes}
        error={outcomesError}
        organizationName={org.displayName}
      />

      {/* Marketplace listings — where credential/capacity/performance facts live */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Store className="w-4 h-4 text-primary" />
              Marketplace listings
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              {passport.marketplace?.listingCount || 0} total
              {listings.length < (passport.marketplace?.listingCount || 0)
                ? ` · ${listings.length} shown`
                : ""}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {listings.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No marketplace listing is linked to this organization. Insurance, bonding,
              capacity and performance facts live on listings, so none are available here.
            </p>
          ) : (
            listings.map((listing) => {
              const verification = verificationDisplay(listing.verificationStatus);
              const age = ageInDays(listing.dataAsOf);
              return (
                <div
                  key={listing.listingId}
                  className="rounded-lg bg-muted/30 p-3 space-y-2 border border-border/50"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      {listingTypeLabel(listing.listingType)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={`${
                          VERIFICATION_TONE[verification.tone] || VERIFICATION_TONE.unknown
                        } font-normal text-[10px]`}
                      >
                        {verification.label}
                      </Badge>
                      {!isVerifiedStatus(listing.verificationStatus) && (
                        <span className="text-[10px] text-muted-foreground">
                          not verified evidence
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Insurance expires</p>
                      <p>{listing.insuranceExpiresOn || <UnknownValue />}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Bonding capacity</p>
                      <p>
                        {typeof listing.bondingCapacity === "number" ? (
                          new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            maximumFractionDigits: 0,
                          }).format(listing.bondingCapacity)
                        ) : (
                          <UnknownValue />
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Crew capacity</p>
                      <p>{listing.capacity?.crewCapacity ?? <UnknownValue />}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Current workload</p>
                      <p>
                        {typeof listing.capacity?.currentWorkloadPct === "number" ? (
                          `${listing.capacity.currentWorkloadPct}%`
                        ) : (
                          <UnknownValue />
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                    {[
                      ["Rating", listing.scores?.rating],
                      ["Safety", listing.scores?.safetyScore],
                      ["Quality", listing.scores?.qualityScore],
                      ["Schedule", listing.scores?.scheduleReliability],
                      ["Projects", listing.scores?.projectsCompleted],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="text-[10px] text-muted-foreground">{label}</p>
                        <p>{value === null || value === undefined ? <UnknownValue /> : value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                    <span>
                      Available{" "}
                      {listing.availability?.availableFrom || "unstated"}
                      {listing.availability?.availableTo
                        ? ` → ${listing.availability.availableTo}`
                        : ""}
                    </span>
                    <span>
                      {age === null ? "Data currency unknown" : `Data ${age} day(s) old`}
                    </span>
                    <span>
                      {(listing.licenses || []).length} licence(s),{" "}
                      {(listing.certifications || []).length} certification(s) listed
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Identity linkage — confirmed vs everything else, never merged */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            Identity linkage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-muted-foreground">
              Confirmed — these records supply every fact above
              {/* Counts are computed over every link; the arrays are capped at
                  50. Printing the count above a shorter list without saying so
                  would read as "that is all of them". */}
              {(linkage.confirmedCount || 0) > (linkage.confirmed || []).length && (
                <span className="font-normal">
                  {" "}
                  · {(linkage.confirmed || []).length} of {linkage.confirmedCount} shown
                </span>
              )}
            </p>
            {(linkage.confirmed || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No confirmed links.</p>
            ) : (
              <div className="space-y-1 pt-1">
                {linkage.confirmed.map((link) => (
                  <div
                    key={link.linkId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded bg-muted/30 px-2 py-1"
                  >
                    <span className="text-xs" title={link.recordId}>
                      {recordTypeLabel(link.recordType)}
                      <span className="text-muted-foreground"> · {shortId(link.recordId)}</span>
                    </span>
                    <div className="flex items-center gap-1.5">
                      {link.recordLoaded === false && (
                        <Badge
                          variant="outline"
                          className={`${VERIFICATION_TONE.bad} font-normal text-[10px]`}
                          title="The link is confirmed but the record behind it could not be loaded."
                        >
                          Record missing
                        </Badge>
                      )}
                      {link.decidedAt && (
                        <span className="text-[10px] text-muted-foreground">
                          confirmed {formatDay(link.decidedAt)}
                        </span>
                      )}
                      {/* A confirmed link can be wrong. The backend allows any
                          state to become DISPUTED or UNRESOLVED, and it is the
                          only way to free a record whose identity was
                          confirmed to the wrong organization — without these
                          the 409 this page reports ("dispute or unresolve it
                          first") would name an action nobody could take. */}
                      {canDecide && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => setDecision({ link, decision: "UNRESOLVED" })}
                          >
                            Unresolve
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => setDecision({ link, decision: "DISPUTED" })}
                          >
                            Dispute
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground">
              Unconfirmed — proposed, disputed or unresolved. These contribute no facts.
              {(linkage.proposedCount || 0) > (linkage.proposed || []).length && (
                <span className="font-normal">
                  {" "}
                  · {(linkage.proposed || []).length} of {linkage.proposedCount} shown
                </span>
              )}
            </p>
            {(linkage.proposed || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing awaiting a decision.</p>
            ) : (
              <div className="space-y-1 pt-1">
                {linkage.proposed.map((link) => {
                  const state = linkStateDisplay(link.state);
                  return (
                    <div
                      key={link.linkId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded bg-muted/30 px-2 py-1.5"
                    >
                      <div className="min-w-0">
                        <span className="text-xs" title={link.recordId}>
                          {recordTypeLabel(link.recordType)}
                          <span className="text-muted-foreground"> · {shortId(link.recordId)}</span>
                        </span>
                        {(link.matchBasis || []).length > 0 && (
                          <p className="text-[10px] text-muted-foreground">
                            Matched on{" "}
                            {link.matchBasis
                              .map((basis) =>
                                basis.kind === "name_similarity"
                                  ? `name similarity ${basis.ratio}`
                                  : String(basis.kind).replaceAll("_", " ")
                              )
                              .join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge
                          variant="outline"
                          className={`${
                            VERIFICATION_TONE[state.tone] || VERIFICATION_TONE.unknown
                          } font-normal text-[10px]`}
                        >
                          {state.label}
                        </Badge>
                        {canDecide && (
                          <>
                            {link.state === "DISPUTED" ? (
                              // DISPUTED → CONFIRMED is refused by the backend.
                              // Unresolving is the only way back, so offer it
                              // rather than leaving the link stranded.
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px]"
                                onClick={() => setDecision({ link, decision: "UNRESOLVED" })}
                              >
                                Unresolve
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px]"
                                onClick={() => setDecision({ link, decision: "CONFIRMED" })}
                              >
                                Confirm
                              </Button>
                            )}
                            {link.state !== "DISPUTED" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[10px]"
                                onClick={() => setDecision({ link, decision: "DISPUTED" })}
                              >
                                Dispute
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {linkage.proposedNote && (
              <p className="text-[10px] text-muted-foreground pt-1">{linkage.proposedNote}</p>
            )}
            {capabilitiesEstablished && !canDecide && (linkage.proposed || []).length > 0 && (
              <p className="text-[10px] text-muted-foreground pt-1">
                Confirming or disputing an identity match is reserved for project managers and
                admins.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Freshness + gaps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Data freshness
            </CardTitle>
          </CardHeader>
          <CardContent>
            {freshness ? (
              <p className="text-sm">
                Newest record {freshness.newestDays} day(s) old; oldest {freshness.oldestDays}{" "}
                day(s).
                <span className="block text-[11px] text-muted-foreground">
                  Across {freshness.counted} timestamped source(s). OMNΛS grades no
                  freshness — these are the stored timestamps.
                </span>
              </p>
            ) : (
              <UnknownValue reason="No linked record or listing carries a usable timestamp." />
            )}
          </CardContent>
        </Card>

        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              What is not known
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0.5">
            {(passport.missingInformation || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nothing was flagged as missing for this organization.
              </p>
            ) : (
              passport.missingInformation.map((line, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  {line}
                </p>
              ))
            )}
            <p className="text-[10px] text-muted-foreground pt-1">
              OMNΛS holds no project history, no relationship history and no CSI/NAICS
              classification on a provider passport. Relationship context appears against a
              specific package in Build My Project.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Identity decision */}
      <Dialog open={!!decision} onOpenChange={(open) => !open && closeDecision()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{copy?.title || "Record identity decision"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{copy?.body}</p>
            {decision && (
              <p className="text-xs text-muted-foreground">
                {recordTypeLabel(decision.link.recordType)} · {shortId(decision.link.recordId)}
              </p>
            )}
            <div>
              <Label className="text-xs" htmlFor="identity-decision-note">
                Note{copy?.noteRequired ? " (required)" : " (optional)"}
              </Label>
              <Textarea
                id="identity-decision-note"
                className="mt-1"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={copy?.placeholder}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closeDecision}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitDecision} disabled={deciding}>
                {deciding && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Record decision
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderPassportPage;
